import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Import feature images
import ragArchitecture from '../assets/images/features/rag-architecture.png';
import securityDashboard from '../assets/images/features/security-dashboard.png';
import integrationFlow from '../assets/images/features/integration-flow.png';
import analyticsView from '../assets/images/features/analytics-view.png';

function Features() {
  useEffect(() => {
    // Inject keyframe animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulseGlow {
        0%, 100% {
          box-shadow: 0 20px 60px rgba(168, 85, 247, 0.4), 0 0 80px rgba(236, 72, 153, 0.2);
          filter: brightness(1);
        }
        50% {
          box-shadow: 0 25px 80px rgba(168, 85, 247, 0.6), 0 0 120px rgba(236, 72, 153, 0.4);
          filter: brightness(1.1);
        }
      }

      @keyframes breatheGlow {
        0%, 100% {
          box-shadow: 0 4px 20px rgba(168, 85, 247, 0.3);
          border-color: rgba(168, 85, 247, 0.3);
        }
        50% {
          box-shadow: 0 8px 40px rgba(168, 85, 247, 0.6), 0 0 60px rgba(236, 72, 153, 0.3);
          border-color: rgba(168, 85, 247, 0.5);
        }
      }

      @keyframes rotateGlow {
        0% {
          box-shadow: 
            0 8px 32px rgba(168, 85, 247, 0.2),
            0 0 40px rgba(168, 85, 247, 0.1);
        }
        25% {
          box-shadow: 
            0 8px 32px rgba(236, 72, 153, 0.3),
            20px 0 40px rgba(168, 85, 247, 0.2);
        }
        50% {
          box-shadow: 
            0 8px 32px rgba(217, 70, 239, 0.3),
            0 20px 60px rgba(236, 72, 153, 0.2);
        }
        75% {
          box-shadow: 
            0 8px 32px rgba(168, 85, 247, 0.3),
            -20px 0 40px rgba(217, 70, 239, 0.2);
        }
        100% {
          box-shadow: 
            0 8px 32px rgba(168, 85, 247, 0.2),
            0 0 40px rgba(168, 85, 247, 0.1);
        }
      }

      @keyframes shimmerGlow {
        0% {
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.5),
            inset 0 0 20px rgba(168, 85, 247, 0);
        }
        50% {
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.5),
            inset 0 0 20px rgba(168, 85, 247, 0.2);
        }
        100% {
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.5),
            inset 0 0 20px rgba(168, 85, 247, 0);
        }
      }

      @keyframes borderGlow {
        0%, 100% {
          border-color: rgba(168, 85, 247, 0.2);
          box-shadow: 0 0 20px rgba(168, 85, 247, 0);
        }
        50% {
          border-color: rgba(168, 85, 247, 0.5);
          box-shadow: 0 0 40px rgba(168, 85, 247, 0.4), inset 0 0 20px rgba(168, 85, 247, 0.1);
        }
      }

      @keyframes textGlow {
        0%, 100% {
          text-shadow: 0 0 40px rgba(168, 85, 247, 0.4);
        }
        50% {
          text-shadow: 
            0 0 60px rgba(168, 85, 247, 0.6),
            0 0 80px rgba(236, 72, 153, 0.4);
        }
      }

      @keyframes tableGlow {
        0%, 100% {
          box-shadow: 0 8px 32px rgba(168, 85, 247, 0.3);
        }
        50% {
          box-shadow: 
            0 12px 48px rgba(168, 85, 247, 0.5),
            0 0 80px rgba(236, 72, 153, 0.3);
        }
      }

      /* Background Light Effects */
      @keyframes floatingLight1 {
        0%, 100% {
          transform: translate(0, 0) scale(1);
          opacity: 0.3;
        }
        33% {
          transform: translate(100px, -50px) scale(1.2);
          opacity: 0.5;
        }
        66% {
          transform: translate(-50px, 100px) scale(0.9);
          opacity: 0.4;
        }
      }

      @keyframes floatingLight2 {
        0%, 100% {
          transform: translate(0, 0) scale(1);
          opacity: 0.4;
        }
        50% {
          transform: translate(-80px, 120px) scale(1.3);
          opacity: 0.6;
        }
      }

      @keyframes floatingLight3 {
        0%, 100% {
          transform: translate(0, 0) scale(1);
          opacity: 0.35;
        }
        40% {
          transform: translate(60px, 80px) scale(1.1);
          opacity: 0.5;
        }
        80% {
          transform: translate(-40px, -60px) scale(0.95);
          opacity: 0.45;
        }
      }

      .animated-glow-image {
        animation: pulseGlow 4s ease-in-out infinite;
        transition: all 0.3s ease;
      }

      .animated-glow-image:hover {
        animation-play-state: paused;
        transform: scale(1.02);
      }

      .animated-glow-badge {
        animation: breatheGlow 3s ease-in-out infinite;
      }

      .animated-glow-metrics {
        animation: rotateGlow 6s ease-in-out infinite;
      }

      .animated-glow-card {
        animation: shimmerGlow 4s ease-in-out infinite;
        transition: all 0.3s ease;
      }

      .animated-glow-card:hover {
        animation: shimmerGlow 2s ease-in-out infinite;
      }

      .animated-glow-platform {
        animation: borderGlow 3s ease-in-out infinite;
      }

      .animated-glow-heading {
        animation: textGlow 4s ease-in-out infinite;
      }

      .animated-glow-table {
        animation: tableGlow 5s ease-in-out infinite;
      }

      .particle-section {
        position: relative;
        overflow: hidden;
      }

      .particle-section > .container {
        position: relative;
        z-index: 1;
      }

      /* Floating Light Orbs in Background */
      .particle-section::after {
        content: '';
        position: absolute;
        width: 500px;
        height: 500px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%);
        top: 20%;
        left: 10%;
        filter: blur(120px);
        animation: floatingLight1 20s ease-in-out infinite;
        z-index: 0;
        pointer-events: none;
      }

      .particle-section::before {
        content: '';
        position: absolute;
        width: 600px;
        height: 600px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%);
        bottom: 15%;
        right: 15%;
        filter: blur(140px);
        animation: floatingLight2 25s ease-in-out infinite;
        z-index: 0;
        pointer-events: none;
      }

      /* Additional light layer for depth */
      .light-effect-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 0;
        overflow: hidden;
      }

      .light-effect-overlay::before {
        content: '';
        position: absolute;
        width: 450px;
        height: 450px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(217, 70, 239, 0.1) 0%, transparent 70%);
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        filter: blur(110px);
        animation: floatingLight3 18s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      {/* Hero Header - Compact with Glow */}
      <section className="page-header particle-section" style={{ 
        padding: '6rem 0 4rem',
        background: '#000000'
      }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h1 data-aos="fade-up" className="animated-glow-heading" style={{ 
            fontSize: '3rem', 
            lineHeight: '1.2',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: '300',
            letterSpacing: '-1px'
          }}>
            Enterprise AI Platform Built for Scale
          </h1>
          <p data-aos="fade-up" data-aos-delay="100" style={{ 
            fontSize: '1.15rem', 
            maxWidth: '650px', 
            margin: '1.25rem auto 2.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontFamily: "'Inter', sans-serif",
            fontWeight: '300'
          }}>
            Production-ready RAG architecture with military-grade security. Deploy in weeks, not months.
          </p>
          
          {/* Key Metrics Bar */}
          <div data-aos="fade-up" data-aos-delay="200" className="animated-glow-metrics" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '2rem',
            maxWidth: '700px',
            margin: '0 auto',
            padding: '2rem 1.5rem',
            background: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '16px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            backdropFilter: 'blur(20px)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '300', 
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>99.9%</div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                marginTop: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>Uptime SLA</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '300', 
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>&lt;100ms</div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                marginTop: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>Response Time</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '300', 
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>200+</div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                marginTop: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>Integrations</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '300', 
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>92%</div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                marginTop: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>Hallucination Reduction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 1: RAG Architecture */}
      <section className="particle-section" style={{ padding: '8rem 0', background: '#000000' }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6rem', 
            alignItems: 'center' 
          }}>
            <div data-aos="fade-right">
              <span className="animated-glow-badge" style={{
                display: 'inline-block',
                background: 'rgba(168, 85, 247, 0.15)',
                color: '#a855f7',
                padding: '0.5rem 1.25rem',
                borderRadius: '50px',
                fontSize: '0.9rem',
                fontWeight: '400',
                marginBottom: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                letterSpacing: '2px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Core AI Engine
              </span>
              <h2 className="animated-glow-heading" style={{ 
                fontSize: '2.75rem', 
                marginBottom: '1.5rem', 
                lineHeight: '1.2',
                color: '#ffffff',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Advanced RAG Architecture
              </h2>
              <p style={{ 
                fontSize: '1.1rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                fontWeight: '300',
                fontFamily: "'Inter', sans-serif"
              }}>
                Retrieval-Augmented Generation with multi-model orchestration for accurate, contextual responses grounded in your enterprise data.
              </p>
              
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  marginBottom: '1rem', 
                  color: '#ffffff',
                  fontWeight: '400',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}>Key Capabilities:</h4>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  display: 'grid',
                  gap: '1rem'
                }}>
                  {[
                    'Semantic search with 99.2% accuracy',
                    'Multi-hop reasoning for complex queries',
                    'Automatic citation & source attribution',
                    'Fine-tuned on domain-specific terminology'
                  ].map((item, idx) => (
                    <li key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '1rem',
                      fontSize: '1rem',
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                        <circle cx="12" cy="12" r="10" fill="rgba(168, 85, 247, 0.2)" />
                        <path d="M9 12l2 2 4-4" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {['GPT-5.1 ', 'Claude Opus 4.5', 'Gemini 3 Pro', 'BYOM'].map((tag, idx) => (
                  <span key={idx} className="animated-glow-platform" style={{ 
                    padding: '0.5rem 1rem', 
                    background: 'rgba(168, 85, 247, 0.12)', 
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: 'rgba(255, 255, 255, 0.85)',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}>{tag}</span>
                ))}
              </div>
            </div>
            
            <div data-aos="fade-left">
              <img 
                src={ragArchitecture} 
                alt="RAG Architecture Diagram" 
                className="animated-glow-image"
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature 2: Security */}
      <section className="particle-section" style={{ 
        padding: '8rem 0', 
        background: '#000000'
      }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6rem', 
            alignItems: 'center' 
          }}>
            <div data-aos="fade-right">
              <img 
                src={securityDashboard} 
                alt="Security Dashboard" 
                className="animated-glow-image"
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              />
            </div>

            <div data-aos="fade-left">
              <span className="animated-glow-badge" style={{
                display: 'inline-block',
                background: 'rgba(168, 85, 247, 0.15)',
                color: '#a855f7',
                padding: '0.5rem 1.25rem',
                borderRadius: '50px',
                fontSize: '0.9rem',
                fontWeight: '400',
                marginBottom: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                letterSpacing: '2px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Security & Compliance
              </span>
              <h2 className="animated-glow-heading" style={{ 
                fontSize: '2.75rem', 
                marginBottom: '1.5rem', 
                lineHeight: '1.2',
                color: '#ffffff',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                ZTA Security Model
              </h2>
              <p style={{ 
                fontSize: '1.1rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                fontWeight: '300',
                fontFamily: "'Inter', sans-serif"
              }}>
                Military-grade encryption with comprehensive compliance frameworks. Your data remains completely under your control with on-premise and VPC deployment options.
              </p>
              
              <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
                {[
                  { 
                    title: 'AES-256 Encryption', 
                    desc: 'End-to-end encryption at rest and TLS 1.3 in transit' 
                  },
                  { 
                    title: 'SOC 2 Type II Certified', 
                    desc: 'Annual audits with ISO 27001, HIPAA, GDPR compliance' 
                  },
                  { 
                    title: 'SSO & RBAC', 
                    desc: 'Okta, Azure AD integration with granular permissions' 
                  }
                ].map((item, idx) => (
                  <div key={idx} className="animated-glow-card" style={{ 
                    padding: '1.5rem',
                    background: 'rgba(168, 85, 247, 0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.2)'
                  }}>
                    <h4 style={{ 
                      fontSize: '1.1rem', 
                      marginBottom: '0.5rem', 
                      color: '#ffffff',
                      fontWeight: '400',
                      fontFamily: "'Space Grotesk', sans-serif"
                    }}>
                      {item.title}
                    </h4>
                    <p style={{ 
                      fontSize: '0.95rem', 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      margin: 0,
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="animated-glow-badge" style={{
                padding: '1.5rem',
                background: 'rgba(168, 85, 247, 0.1)',
                borderRadius: '12px',
                borderLeft: '4px solid #a855f7',
                border: '1px solid rgba(168, 85, 247, 0.3)'
              }}>
                <p style={{ 
                  fontSize: '0.95rem', 
                  color: 'rgba(255, 255, 255, 0.85)', 
                  margin: 0,
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <strong>Data Sovereignty:</strong> Deploy on your own infrastructure with complete control over data residency and retention policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3: Integration */}
      <section className="particle-section" style={{ padding: '8rem 0', background: '#000000' }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6rem', 
            alignItems: 'center' 
          }}>
            <div data-aos="fade-right">
              <span className="animated-glow-badge" style={{
                display: 'inline-block',
                background: 'rgba(168, 85, 247, 0.15)',
                color: '#a855f7',
                padding: '0.5rem 1.25rem',
                borderRadius: '50px',
                fontSize: '0.9rem',
                fontWeight: '400',
                marginBottom: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                letterSpacing: '2px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Deployment & Integration
              </span>
              <h2 className="animated-glow-heading" style={{ 
                fontSize: '2.75rem', 
                marginBottom: '1.5rem', 
                lineHeight: '1.2',
                color: '#ffffff',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                API-First Architecture
              </h2>
              <p style={{ 
                fontSize: '1.1rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                fontWeight: '300',
                fontFamily: "'Inter', sans-serif"
              }}>
                Connect to 200+ enterprise systems with pre-built connectors. Real-time bidirectional sync with webhooks and event streaming.
              </p>

              <div style={{ marginBottom: '2.5rem' }}>
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  marginBottom: '1.25rem', 
                  color: '#ffffff',
                  fontWeight: '400',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}>
                  Supported Platforms:
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {[
                    'Salesforce CRM',
                    'Microsoft SharePoint',
                    'Google Workspace',
                    'Slack & Teams',
                    'Confluence & Jira',
                    'Custom Databases',
                    'AWS S3 / Azure Blob',
                    'REST / GraphQL APIs'
                  ].map((platform, idx) => (
                    <div key={idx} className="animated-glow-platform" style={{
                      padding: '0.75rem 1rem',
                      background: 'rgba(168, 85, 247, 0.08)',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      color: 'rgba(255, 255, 255, 0.85)',
                      border: '1px solid rgba(168, 85, 247, 0.2)',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {platform}
                    </div>
                  ))}
                </div>
              </div>

              <div className="animated-glow-badge" style={{ 
                padding: '2rem', 
                background: 'rgba(168, 85, 247, 0.1)', 
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.3)'
              }}>
                <p style={{ 
                  fontSize: '1rem', 
                  color: 'rgba(255, 255, 255, 0.85)', 
                  margin: 0,
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <strong>Kubernetes-Native:</strong> Auto-scaling, load balancing, and multi-region deployment with 99.9% uptime SLA.
                </p>
              </div>
            </div>
            
            <div data-aos="fade-left">
              <img 
                src={integrationFlow} 
                alt="Integration Architecture" 
                className="animated-glow-image"
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature 4: Analytics */}
      <section className="particle-section" style={{ 
        padding: '8rem 0', 
        background: '#000000'
      }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6rem', 
            alignItems: 'center' 
          }}>
            <div data-aos="fade-right">
              <img 
                src={analyticsView} 
                alt="Analytics Dashboard" 
                className="animated-glow-image"
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              />
            </div>

            <div data-aos="fade-left">
              <span className="animated-glow-badge" style={{
                display: 'inline-block',
                background: 'rgba(168, 85, 247, 0.15)',
                color: '#a855f7',
                padding: '0.5rem 1.25rem',
                borderRadius: '50px',
                fontSize: '0.9rem',
                fontWeight: '400',
                marginBottom: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                letterSpacing: '2px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Monitoring & Insights
              </span>
              <h2 className="animated-glow-heading" style={{ 
                fontSize: '2.75rem', 
                marginBottom: '1.5rem', 
                lineHeight: '1.2',
                color: '#ffffff',
                fontWeight: '300',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                Real-Time Observability
              </h2>
              <p style={{ 
                fontSize: '1.1rem', 
                color: 'rgba(255, 255, 255, 0.7)', 
                lineHeight: '1.8', 
                marginBottom: '2.5rem',
                fontWeight: '300',
                fontFamily: "'Inter', sans-serif"
              }}>
                Track every query, measure ROI, and optimize performance with comprehensive analytics and monitoring dashboards.
              </p>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {[
                  { metric: 'Sub-100ms', label: 'P50 Response Time', icon: '⚡' },
                  { metric: '99.9%', label: 'System Uptime', icon: '🎯' },
                  { metric: '200K+', label: 'Daily Queries', icon: '📊' },
                  { metric: '$0.02', label: 'Cost Per Query', icon: '💰' }
                ].map((item, idx) => (
                  <div key={idx} className="animated-glow-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5rem',
                    padding: '1.5rem',
                    background: 'rgba(168, 85, 247, 0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.2)'
                  }}>
                    <div style={{ fontSize: '2rem' }}>{item.icon}</div>
                    <div>
                      <div style={{ 
                        fontSize: '1.75rem', 
                        fontWeight: '300', 
                        background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: "'Space Grotesk', sans-serif"
                      }}>
                        {item.metric}
                      </div>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        {item.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Matrix Table */}
      <section className="particle-section" style={{ padding: '8rem 0', background: '#000000' }}>
        <div className="light-effect-overlay"></div>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span className="animated-glow-badge" style={{
              display: 'inline-block',
              background: 'rgba(168, 85, 247, 0.15)',
              color: '#a855f7',
              padding: '0.5rem 1.25rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '400',
              marginBottom: '1.5rem',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              letterSpacing: '2px',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              Platform Capabilities
            </span>
            <h2 className="animated-glow-heading" style={{ 
              fontSize: '2.75rem', 
              marginBottom: '1rem',
              color: '#ffffff',
              fontWeight: '300',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              Complete Feature Matrix
            </h2>
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'rgba(255, 255, 255, 0.7)', 
              maxWidth: '600px', 
              margin: '0 auto',
              fontFamily: "'Inter', sans-serif"
            }}>
              Everything you need for enterprise AI deployment
            </p>
          </div>

          <div data-aos="fade-up">
            <table className="animated-glow-table" style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0',
              background: 'rgba(10, 10, 10, 0.8)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid rgba(168, 85, 247, 0.2)'
            }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)', color: 'white' }}>
                  <th style={{ 
                    padding: '1.5rem 2rem', 
                    textAlign: 'left', 
                    fontSize: '1.05rem', 
                    fontWeight: '400',
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}>Capability</th>
                  <th style={{ 
                    padding: '1.5rem 2rem', 
                    textAlign: 'left', 
                    fontSize: '1.05rem', 
                    fontWeight: '400',
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}>Specification</th>
                  <th style={{ 
                    padding: '1.5rem', 
                    textAlign: 'center', 
                    width: '100px', 
                    fontSize: '1.05rem', 
                    fontWeight: '400',
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { capability: 'RAG Architecture', spec: 'Multi-model orchestration with semantic search', status: '✓' },
                  { capability: 'Document Processing', spec: '50+ formats, OCR, handwriting recognition', status: '✓' },
                  { capability: 'Real-time Sync', spec: '200+ integrations, bidirectional webhooks', status: '✓' },
                  { capability: 'Model Fine-tuning', spec: 'Custom training on enterprise data', status: '✓' },
                  { capability: 'Deployment Options', spec: 'Cloud, on-premise, hybrid VPC', status: '✓' },
                  { capability: 'Security Compliance', spec: 'SOC 2, ISO 27001, HIPAA, GDPR', status: '✓' },
                  { capability: 'SSO Integration', spec: 'Okta, Azure AD, Google Workspace', status: '✓' },
                  { capability: 'API Access', spec: 'REST, GraphQL, native SDKs', status: '✓' },
                  { capability: 'Support SLA', spec: '24/7 with <15min response time', status: '✓' },
                  { capability: 'White Label', spec: 'Custom branding & domain', status: '✓' }
                ].map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: idx !== 9 ? '1px solid rgba(168, 85, 247, 0.1)' : 'none' }}>
                    <td style={{ 
                      padding: '1.5rem 2rem', 
                      fontWeight: '400', 
                      color: '#ffffff',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {row.capability}
                    </td>
                    <td style={{ 
                      padding: '1.5rem 2rem', 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      fontSize: '0.95rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {row.spec}
                    </td>
                    <td style={{ padding: '1.5rem', textAlign: 'center' }}>
                      <span style={{ 
                        color: '#10b981', 
                        fontSize: '1.5rem',
                        display: 'inline-block',
                        width: '32px',
                        height: '32px',
                        lineHeight: '32px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        borderRadius: '50%'
                      }}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content container" data-aos="fade-up">
          <h2 style={{ 
            fontSize: '3rem',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>Ready to Transform Your Enterprise?</h2>
          <p style={{ 
            fontSize: '1.25rem', 
            maxWidth: '700px', 
            margin: '1.5rem auto 2.5rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Schedule a personalized demo and see how our platform delivers ROI in weeks, not months.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="button-large glow-effect" style={{ 
              fontSize: '1.1rem', 
              padding: '1rem 2.5rem',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              Request Demo
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/pricing" className="button-large" style={{ 
              background: 'rgba(168, 85, 247, 0.15)', 
              color: '#ffffff', 
              fontSize: '1.1rem', 
              padding: '1rem 2.5rem',
              border: '2px solid rgba(168, 85, 247, 0.5)',
              fontFamily: "'Space Grotesk', sans-serif"
            }}>
              View Pricing
            </Link>
          </div>
          <div style={{ 
            marginTop: '2.5rem', 
            fontSize: '1rem', 
            opacity: 0.9,
            fontFamily: "'Inter', sans-serif"
          }}>
            ✓ No credit card required  •  ✓ 14-day free trial  •  ✓ Deploy in under 1 hour
          </div>
        </div>
        <div className="cta-particles"></div>
      </section>
    </>
  );
}

export default Features;
